import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  NOTIFICATION_DURATION_MS,
  type AppNotification,
  type AppNotificationKind,
} from "./notification-types.js";
import { evictNotificationsForCapacity } from "./notification-stack-policy.js";
import {
  dismissedImportProgressJobIdsAfterContinueInBackground,
  dismissedImportProgressJobIdsAfterDialogOpen,
  dismissedImportProgressJobIdsAfterDismiss,
  dismissedImportProgressJobIdsAfterTerminal,
  decideImportNotificationUpsert,
} from "./import-notification-upsert.js";

type PushInput = {
  kind?: AppNotificationKind;
  message: string;
  detail?: string;
  durationMs?: number | null;
  jobId?: string;
  processedRows?: number;
  totalRows?: number | null;
  createdCount?: number;
  restoredCount?: number;
  skippedCount?: number;
  failedCount?: number;
  status?: string;
};

type NotificationContextValue = {
  visible: AppNotification[];
  pushNotification: (input: PushInput) => string;
  dismissNotification: (id: string) => void;
  upsertImportNotification: (input: PushInput & { jobId: string }) => void;
  clearImportNotification: (jobId: string) => void;
  hideImportProgressToast: (jobId: string) => void;
  revealImportProgressToast: (jobId: string) => void;
};

const NotificationContext = createContext<NotificationContextValue | null>(null);

let nextId = 0;

function createId(): string {
  nextId += 1;
  return `n-${nextId}`;
}

export function NotificationProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [items, setItems] = useState<AppNotification[]>([]);
  const timers = useRef(new Map<string, number>());
  const dismissedProgressJobIds = useRef(new Set<string>());

  const clearTimer = useCallback((id: string) => {
    const timer = timers.current.get(id);
    if (timer != null) {
      window.clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const dismissNotification = useCallback((id: string) => {
    setItems((current) => {
      const item = current.find((entry) => entry.id === id);
      if (item) {
        dismissedProgressJobIds.current = dismissedImportProgressJobIdsAfterDismiss(
          dismissedProgressJobIds.current,
          item,
        );
      }
      clearTimer(id);
      return current.filter((entry) => entry.id !== id);
    });
  }, [clearTimer]);

  const scheduleDismiss = useCallback(
    (id: string, durationMs: number | null) => {
      clearTimer(id);
      if (durationMs == null) return;
      const timer = window.setTimeout(() => dismissNotification(id), durationMs);
      timers.current.set(id, timer);
    },
    [clearTimer, dismissNotification],
  );

  const replaceItems = useCallback(
    (current: AppNotification[], next: AppNotification[]): AppNotification[] => {
      const kept = new Set(next.map((item) => item.id));
      for (const item of current) {
        if (!kept.has(item.id)) clearTimer(item.id);
      }
      return evictNotificationsForCapacity(next);
    },
    [clearTimer],
  );

  const pushNotification = useCallback(
    (input: PushInput): string => {
      const id = createId();
      const durationMs = input.durationMs === undefined ? NOTIFICATION_DURATION_MS : input.durationMs;
      const nextItem: AppNotification = {
        id,
        kind: input.kind ?? "success",
        message: input.message,
        detail: input.detail,
        createdAt: Date.now(),
        durationMs,
        jobId: input.jobId,
        processedRows: input.processedRows,
        totalRows: input.totalRows,
        createdCount: input.createdCount,
        restoredCount: input.restoredCount,
        skippedCount: input.skippedCount,
        failedCount: input.failedCount,
        status: input.status,
      };
      setItems((current) => replaceItems(current, [nextItem, ...current]));
      scheduleDismiss(id, durationMs);
      return id;
    },
    [replaceItems, scheduleDismiss],
  );

  const upsertImportNotification = useCallback(
    (input: PushInput & { jobId: string }) => {
      setItems((current) => {
        if (input.status) {
          dismissedProgressJobIds.current = dismissedImportProgressJobIdsAfterTerminal(
            dismissedProgressJobIds.current,
            input.jobId,
            input.status,
          );
        }
        const existing = current.find((item) => item.jobId === input.jobId);
        const decision = decideImportNotificationUpsert({
          dismissedJobIds: dismissedProgressJobIds.current,
          jobId: input.jobId,
          status: input.status ?? "",
          hasVisibleToast: existing != null,
        });
        if (decision === "skip") return current;

        const durationMs = input.durationMs === undefined ? null : input.durationMs;
        if (decision === "update" && existing) {
          scheduleDismiss(existing.id, durationMs);
          return current.map((item) =>
            item.id === existing.id
              ? {
                  ...item,
                  message: input.message,
                  detail: input.detail,
                  durationMs,
                  processedRows: input.processedRows,
                  totalRows: input.totalRows,
                  createdCount: input.createdCount,
                  restoredCount: input.restoredCount,
                  skippedCount: input.skippedCount,
                  failedCount: input.failedCount,
                  status: input.status,
                }
              : item,
          );
        }
        const id = createId();
        scheduleDismiss(id, durationMs);
        return replaceItems(current, [
          {
            id,
            kind: "import",
            message: input.message,
            detail: input.detail,
            createdAt: Date.now(),
            durationMs,
            jobId: input.jobId,
            processedRows: input.processedRows,
            totalRows: input.totalRows,
            createdCount: input.createdCount,
            restoredCount: input.restoredCount,
            skippedCount: input.skippedCount,
            failedCount: input.failedCount,
            status: input.status,
          },
          ...current,
        ]);
      });
    },
    [replaceItems, scheduleDismiss],
  );

  const clearImportNotification = useCallback((jobId: string) => {
    setItems((current) => {
      const match = current.find((item) => item.jobId === jobId);
      if (match) clearTimer(match.id);
      return current.filter((item) => item.jobId !== jobId);
    });
  }, [clearTimer]);

  const hideImportProgressToast = useCallback((jobId: string) => {
    dismissedProgressJobIds.current = dismissedImportProgressJobIdsAfterDialogOpen(
      dismissedProgressJobIds.current,
      jobId,
    );
    clearImportNotification(jobId);
  }, [clearImportNotification]);

  const revealImportProgressToast = useCallback((jobId: string) => {
    dismissedProgressJobIds.current = dismissedImportProgressJobIdsAfterContinueInBackground(
      dismissedProgressJobIds.current,
      jobId,
    );
  }, []);

  const value = useMemo(
    () => ({
      visible: items,
      pushNotification,
      dismissNotification,
      upsertImportNotification,
      clearImportNotification,
      hideImportProgressToast,
      revealImportProgressToast,
    }),
    [
      items,
      pushNotification,
      dismissNotification,
      upsertImportNotification,
      clearImportNotification,
      hideImportProgressToast,
      revealImportProgressToast,
    ],
  );

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
}

export function useNotifications(): NotificationContextValue {
  const value = useContext(NotificationContext);
  if (!value) {
    throw new Error("useNotifications must be used within NotificationProvider");
  }
  return value;
}

export function useOptionalNotifications(): NotificationContextValue | null {
  return useContext(NotificationContext);
}
